/*
 * 上传前的本地校验。
 *
 * 背景：PRD 第 8 章规定服务端按文件头判类型、40 MB 与 8.3 MP 两道上限，
 *   超限「直接拒绝并说明原因，不缩放、不静默截断」（PRD 1.3）。
 * 设计意图：同样的三项在浏览器里先做一遍。不是为了替代服务端——**服务端那一遍照做**，
 *   这里挡掉的是「传完 30 MB 才被告知超像素」这段白等。4K 手机热点上传要几十秒，
 *   而拒收的判据（文件头、字节数、宽高）在本地一次解码就全知道了。
 * 关键约束：
 *   1. 返回的错误形状与 PRD 第 6 章的服务端错误信封一致（code / message / hint），
 *      于是页面渲染错误的那段代码不必分辨这条是本地判的还是服务端判的。
 *   2. 超像素的文案必须同时说出「你这张是多少、上限是多少、为什么不替你缩放」，
 *      这是 PRD 第 6 章点名要求的三件事。少说第三件，用户只会觉得是我们懒。
 *   3. 类型判定读前 16 个字节，不看扩展名也不看 File.type——那两个都是客户端可编的。
 *      本地这一遍仍然只是抢跑，服务端不能因为前端做过就省掉。
 */

/** 像素上限。与服务端 8.3 MP 的口径一致（PRD 4.1 大图通道）。 */
export const MAX_PIXELS = 8_300_000
/** 上传体积上限，与 PRD 第 8 章一致。 */
export const MAX_BYTES = 40 * 1024 * 1024

/**
 * PRD 第 6 章的错误信封：message 是中文，hint 给可操作的下一步。
 *
 * 本地拒收与服务端报错共用这一种形状，所以页面渲染错误只有一段代码。code 是开放的
 * 字符串：服务端的错误码会随功能长出新的（余额不足、票据过期、磁盘满……），
 * 把它写成闭合联合，等于每加一种后端错误就要改一次前端类型，而页面并不按 code 分支。
 */
export interface PickError {
  code: string
  message: string
  hint: string
}

/** 校验通过的图。objectUrl 由调用方在换图或卸载时 revoke。 */
export interface PickedImage {
  file: File
  name: string
  bytes: number
  pixels: [number, number]
  /** 浏览器解出来的类型，取自文件头而不是扩展名。 */
  type: 'PNG' | 'JPEG' | 'WebP'
  objectUrl: string
}

const MB = (n: number) => `${(n / 1024 / 1024).toFixed(1)} MB`
const MP = (w: number, h: number) => ((w * h) / 1_000_000).toFixed(2)

/**
 * 读前 16 字节判类型。
 *
 * PNG：89 50 4E 47 0D 0A 1A 0A；JPEG：FF D8 FF；WebP：RIFF....WEBP。
 * 这三种之外一律拒收，包括 HEIC 与 AVIF：管线的解码栈只装了这三种，
 * 前端放行只会让用户多等一次往返再看到同一句拒绝。
 */
async function sniff(file: File): Promise<PickedImage['type'] | null> {
  const b = new Uint8Array(await file.slice(0, 16).arrayBuffer())
  if (b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) return 'PNG'
  if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return 'JPEG'
  const ascii = (i: number, s: string) => s.split('').every((c, k) => b[i + k] === c.charCodeAt(0))
  if (ascii(0, 'RIFF') && ascii(8, 'WEBP')) return 'WebP'
  return null
}

/** 解码只为拿宽高。用 Image 而不是 createImageBitmap：后者在部分 Safari 上不吃 WebP。 */
function decode(url: string): Promise<[number, number]> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve([img.naturalWidth, img.naturalHeight])
    img.onerror = () => reject(new Error('decode failed'))
    img.src = url
  })
}

export async function pickImage(file: File): Promise<{ ok: PickedImage } | { err: PickError }> {
  const type = await sniff(file)
  if (!type) {
    return {
      err: {
        code: 'unsupported_type',
        message: `这个文件不是 PNG、JPEG 或 WebP（按文件头判定，不看扩展名）。`,
        hint: '手机拍的 HEIC 和相机的 RAW 都要先转成这三种之一再上传。',
      },
    }
  }

  if (file.size > MAX_BYTES) {
    return {
      err: {
        code: 'too_large_bytes',
        message: `这个文件 ${MB(file.size)}，超过 ${MB(MAX_BYTES)} 的上传上限。`,
        hint: 'PNG 转成质量 95 的 JPEG 通常能小一个数量级，纹理诊断不受这一步影响。',
      },
    }
  }

  const objectUrl = URL.createObjectURL(file)
  let w: number
  let h: number
  try {
    ;[w, h] = await decode(objectUrl)
  } catch {
    URL.revokeObjectURL(objectUrl)
    return {
      err: {
        code: 'decode_failed',
        message: '这张图打不开，可能在传输中损坏了。',
        hint: '换一张，或者用看图软件另存一遍再传。',
      },
    }
  }

  if (w * h > MAX_PIXELS) {
    URL.revokeObjectURL(objectUrl)
    return {
      err: {
        code: 'too_many_pixels',
        message:
          `你这张是 ${w}×${h}（${MP(w, h)} MP），超过 ${(MAX_PIXELS / 1_000_000).toFixed(1)} MP 的上限。` +
          '我们按原始像素做全幅分析，不会替你缩放：要检测的纹理周期只有 2 到 4 像素，' +
          '一旦重采样就会先被抹掉，缩放后的图必然被判成干净的。',
        hint: '请自己裁切或另存到上限以内再上传，裁切不影响诊断——纹理是全幅均匀的。',
      },
    }
  }

  return { ok: { file, name: file.name, bytes: file.size, pixels: [w, h], type, objectUrl } }
}
